/**
 * useSupport — Landlord
 * Manages support conversation, availability, and contact form state
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSupportConversationSubscription } from './useCentrifugo';
import {
  getSupportAvailability,
  getSupportConversation,
  sendSupportMessage,
  listSupportMessages,
  submitSupportContact,
  type SupportAvailability,
  type SupportConversationSummary,
  type SupportMessageSummary,
} from '../lib/api';

export interface UseSupportReturn {
  availability: SupportAvailability | null;
  conversation: SupportConversationSummary | null;
  messages: SupportMessageSummary[];
  sendMessage: (content: string) => Promise<void>;
  submitContactForm: (subject: string, message: string) => Promise<void>;
  contactFormSuccess: boolean;
  isLoading: boolean;
  error: string | null;
}

export function useSupport(): UseSupportReturn {
  const [availability, setAvailability] = useState<SupportAvailability | null>(null);
  const [conversation, setConversation] = useState<SupportConversationSummary | null>(null);
  const [messages, setMessages] = useState<SupportMessageSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contactFormSuccess, setContactFormSuccess] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    async function load() {
      try {
        setIsLoading(true);
        setError(null);

        const [availResult, convResult] = await Promise.all([
          getSupportAvailability(),
          getSupportConversation(),
        ]);

        setAvailability(availResult);
        setConversation(convResult);

        // Load existing messages
        const msgResult = await listSupportMessages();
        setMessages(msgResult.messages);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load support data');
      } finally {
        setIsLoading(false);
      }
    }

    load();
  }, []);

  // Real-time subscription on support:conversation:<id> channel
  useSupportConversationSubscription(
    conversation?.id ?? null,
    useCallback((message: SupportMessageSummary) => {
      setMessages((prev) => {
        if (prev.some((m) => m.id === message.id)) return prev;
        return [...prev, message];
      });
    }, [])
  );

  const sendMessage = useCallback(async (content: string) => {
    const { message } = await sendSupportMessage(content);
    setMessages((prev) => {
      if (prev.some((m) => m.id === message.id)) return prev;
      return [...prev, message];
    });
  }, []);

  const submitContactForm = useCallback(async (subject: string, message: string) => {
    await submitSupportContact(subject, message);
    setContactFormSuccess(true);
  }, []);

  return {
    availability,
    conversation,
    messages,
    sendMessage,
    submitContactForm,
    contactFormSuccess,
    isLoading,
    error,
  };
}
